<template>
  <div class="post-page">
    <HeadNav selectedLanguage="en" page="blogs" />

    <main v-if="post">
      <article class="post-article">
        <header class="post-hero">
          <p class="post-kicker">SuperVPN Guide</p>
          <h1>{{ post.title }}</h1>
          <time :datetime="post.date">{{ formattedDate }}</time>
        </header>

        <figure class="post-cover">
          <img :src="post.thumbnail || post.poster" :alt="post.imageAlt" width="640" height="360" loading="eager">
        </figure>

        <section class="post-intro">
          <p v-for="paragraph in post.introHtml" :key="paragraph" v-html="paragraph"></p>
        </section>

        <section v-for="section in post.sections" :key="section.heading" class="post-section">
          <h2>{{ section.heading }}</h2>
          <template v-for="(block, blockIndex) in sectionBlocks(section)">
            <p
              v-if="block.type === 'html'"
              :key="`${section.heading}-p-${blockIndex}`"
              v-html="block.html"
            ></p>
            <ul
              v-else-if="block.type === 'list'"
              :key="`${section.heading}-ul-${blockIndex}`"
              class="post-list"
            >
              <li v-for="item in block.items" :key="item" v-html="item"></li>
            </ul>
          </template>
        </section>
      </article>

      <section class="latest-posts" aria-labelledby="latest-posts-title">
        <div class="latest-posts__inner">
          <h2 id="latest-posts-title">Latest Posts</h2>
          <div class="latest-posts__grid">
            <router-link
              v-for="item in latestPosts"
              :key="item.path"
              class="latest-card"
              :to="item.path"
            >
              <img :src="item.thumbnail || item.poster" :alt="item.imageAlt" width="640" height="360" loading="lazy">
              <span class="latest-card__body">
                <time class="latest-card__date" :datetime="item.date">{{ formatPostDate(item.date) }}</time>
                <span class="latest-card__title">{{ item.title }}</span>
                <span class="latest-card__desc">{{ item.description }}</span>
                <span class="latest-card__more">Read guide</span>
              </span>
            </router-link>
          </div>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import '@/css/base.scss';
import HeadNav from '@/components/HeadNav.vue';
import { getLatestPosts, getPostByPath } from '@/data/posts.js';

export default {
  name: 'PostPage',
  components: {
    HeadNav,
  },
  computed: {
    post() {
      return getPostByPath(this.$route.path);
    },
    latestPosts() {
      if (!this.post) return [];
      return getLatestPosts(3, this.post.path);
    },
    formattedDate() {
      if (!this.post || !this.post.date) return '';
      return this.formatPostDate(this.post.date);
    },
  },
  methods: {
    sectionBlocks(section) {
      if (section.blocks && section.blocks.length) {
        return section.blocks;
      }
      const blocks = (section.body || []).map((html) => ({ type: 'html', html }));
      if (section.list && section.list.length) {
        blocks.push({
          type: 'list',
          items: section.list.map((item) => item),
        });
      }
      return blocks;
    },
    formatPostDate(date) {
      if (!date) return '';
      return new Date(`${date}T00:00:00Z`).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import url('https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&family=Sora:wght@500;600;700&display=swap');

.post-page {
  --page: #f3f7fb;
  --surface: #ffffff;
  --ink: #12263a;
  --body: #445568;
  --muted: #667789;
  --line: #d5e4ef;
  --brand: #1aade8;
  --brand-dark: #0d84b8;
  --brand-soft: rgba(26, 173, 232, 0.12);
  --band: #e7f5fc;
  --shadow: 0 18px 40px rgba(18, 38, 58, 0.08);
  min-height: 100vh;
  background: linear-gradient(180deg, #f7fbfd 0%, var(--page) 42%, #eef3f8 100%);
  color: var(--body);
  font-family: 'Manrope', 'Segoe UI', sans-serif;
}

.post-article {
  width: min(900px, calc(100% - 48px));
  margin: 0 auto;
  padding: 54px 0 36px;
}

.post-hero {
  text-align: center;
}

.post-kicker {
  margin: 0 0 12px;
  color: var(--brand-dark);
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: uppercase;
}

h1,
h2 {
  margin: 0;
  color: var(--ink);
  font-family: 'Sora', 'Manrope', sans-serif;
  letter-spacing: 0;
}

h1 {
  font-size: clamp(32px, 4vw, 48px);
  line-height: 1.14;
}

.post-hero time {
  display: inline-flex;
  margin-top: 18px;
  color: var(--muted);
  font-size: 14px;
  font-weight: 700;
}

.post-cover {
  display: flex;
  justify-content: center;
  margin: 36px 0 30px;
}

.post-cover img {
  width: min(100%, 760px);
  aspect-ratio: 16 / 9;
  height: auto;
  display: block;
  border: 1px solid rgba(26, 173, 232, 0.16);
  border-radius: 16px;
  object-fit: cover;
  background: #ffffff;
  box-shadow: var(--shadow);
}

.post-intro {
  margin-top: 34px;
  padding-top: 28px;
  border-top: 1px solid var(--line);
}

.post-section {
  margin-top: 34px;
}

.post-section h2 {
  margin-bottom: 14px;
  font-size: clamp(24px, 2.8vw, 30px);
  line-height: 1.28;
}

p,
li {
  font-size: 17px;
  line-height: 1.82;
}

p {
  margin: 0 0 18px;
}

p:last-child {
  margin-bottom: 0;
}

.post-article ::v-deep a {
  color: #116dff;
  font-weight: 800;
  text-decoration: underline;
  text-decoration-color: rgba(17, 109, 255, 0.45);
  text-decoration-thickness: 2px;
  text-underline-offset: 4px;
}

.post-article ::v-deep a:visited {
  color: #116dff;
}

.post-article ::v-deep a:hover,
.post-article ::v-deep a:focus-visible {
  color: #0046c7;
  text-decoration-color: currentColor;
}

.post-article ::v-deep strong {
  color: var(--ink);
  font-weight: 800;
}

.post-list {
  margin: 10px 0 0;
}

.post-list li {
  margin: 10px 0;
}

.post-list li::marker {
  color: #116dff;
}

.latest-posts {
  padding: 52px 0 72px;
  background: linear-gradient(180deg, #eef6fb 0%, #f3f7fb 48%, #eef3f8 100%);
  border-top: 1px solid rgba(26, 173, 232, 0.12);
}

.latest-posts__inner {
  width: min(1080px, calc(100% - 48px));
  margin: 0 auto;
}

.latest-posts h2 {
  margin: 0 0 28px;
  color: var(--ink);
  font-family: 'Sora', 'Manrope', sans-serif;
  font-size: clamp(24px, 3vw, 32px);
  font-weight: 700;
  line-height: 1.2;
  text-align: center;
}

.latest-posts__grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 20px;
  align-items: stretch;
}

.latest-card {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 16px;
  border: 1px solid rgba(26, 173, 232, 0.14);
  border-left: 4px solid var(--brand);
  border-radius: 10px;
  background: linear-gradient(180deg, var(--surface) 0%, #f8fbfe 100%);
  color: inherit;
  text-decoration: none !important;
  box-shadow: 0 10px 28px rgba(18, 38, 58, 0.05);
  transition: transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease;
}

.latest-card:hover {
  border-color: rgba(26, 173, 232, 0.35);
  text-decoration: none;
  transform: translateY(-3px);
  box-shadow: var(--shadow);
}

.latest-card img {
  width: 100%;
  aspect-ratio: 16 / 9;
  height: auto;
  display: block;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid rgba(26, 173, 232, 0.12);
  background: #ffffff;
}

.latest-card__body {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
  padding-top: 14px;
}

.latest-card__date {
  color: var(--muted);
  font-size: 13px;
  font-weight: 700;
  line-height: 1.3;
}

.latest-card__title {
  display: -webkit-box;
  margin-top: 8px;
  overflow: hidden;
  color: var(--ink);
  font-family: 'Sora', 'Manrope', sans-serif;
  font-size: 17px;
  font-weight: 700;
  line-height: 1.35;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
}

.latest-card__desc {
  display: -webkit-box;
  margin-top: 8px;
  overflow: hidden;
  color: var(--muted);
  font-size: 14px;
  line-height: 1.55;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
}

.latest-card__more {
  margin-top: auto;
  padding-top: 14px;
  color: var(--brand-dark);
  font-size: 14px;
  font-weight: 800;
}

@media (max-width: 820px) {
  .post-article,
  .latest-posts__inner {
    width: min(100% - 28px, 900px);
  }

  .post-article {
    padding-top: 38px;
  }

  .latest-posts__grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }
}

@media (min-width: 821px) and (max-width: 1100px) {
  .latest-posts__grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 560px) {
  p,
  li {
    font-size: 16px;
  }
}
</style>
